import React, { useContext, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { configData } from "../../services/api-config";
import { AuthContext } from "../../context/auth.context";
import { Link, useNavigate, useLocation } from "react-router-dom";
import CustomInput from "../../components/custom/custom-input/CustomInput";
import styles from "./LoginPage.module.scss";
import CustomButton from "../../components/custom/custom-button/CustomButton";
import { LanguageDropdown } from "../../components/LanguageSelector/LanguageSelector";
import CopyIconNew from "../../components/shared/icons/CopyIconNew";
import { Check } from "lucide-react";
import { toast } from "react-toastify";
import { User } from "../../services";

const invitationRequests = new Map();

const getInvitationRequest = (code) => {
  if (!invitationRequests.has(code)) {
    invitationRequests.set(code, {
      handled: false,
      promise: Promise.resolve().then(() => User.acceptPartnerInvitation(code)),
    });
  }

  return invitationRequests.get(code);
};

const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useContext(AuthContext);
  const usernameInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const processedInvitationCodeRef = useRef(null);
  const isInvitationEffectActiveRef = useRef(false);
  const loginRequestRef = useRef(null);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const [errorPassword, setErrorPassword] = useState();
  const [invalidEmail, setInvalidEmail] = useState();

  const [isLoading, setIsLoading] = useState(false);
  const [copiedUsername, setCopiedUsername] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);

  useEffect(
    () => () => {
      loginRequestRef.current?.abort();
      loginRequestRef.current = null;
    },
    [],
  );

  useEffect(() => {
    if (location.state?.username) setUsername(location.state.username);
    if (location.state?.password) setPassword(location.state.password);
  }, [location.state]);

  useEffect(() => {
    isInvitationEffectActiveRef.current = true;
    const searchParams = new URLSearchParams(location.search);
    const invitationCode = searchParams.get("code");

    if (!invitationCode || processedInvitationCodeRef.current === invitationCode) {
      return () => {
        isInvitationEffectActiveRef.current = false;
      };
    }

    processedInvitationCodeRef.current = invitationCode;
    const invitationRequest = getInvitationRequest(invitationCode);
    invitationRequest.promise
      .then(() => {
        if (
          !isInvitationEffectActiveRef.current ||
          processedInvitationCodeRef.current !== invitationCode ||
          invitationRequest.handled
        ) {
          return;
        }

        invitationRequest.handled = true;
        toast.success(t("loginPage.invitation.success"));
        searchParams.delete("code");
        const nextSearch = searchParams.toString();
        navigate(
          {
            pathname: location.pathname,
            search: nextSearch ? `?${nextSearch}` : "",
            hash: location.hash,
          },
          { replace: true, state: location.state },
        );
      })
      .catch(() => {
        if (
          !isInvitationEffectActiveRef.current ||
          processedInvitationCodeRef.current !== invitationCode ||
          invitationRequest.handled
        ) {
          return;
        }

        invitationRequest.handled = true;
        toast.error(t("loginPage.invitation.error"));
      });

    return () => {
      isInvitationEffectActiveRef.current = false;
    };
  }, [location.hash, location.pathname, location.search, location.state, navigate, t]);

  const handleLogin = () => {
    if (loginRequestRef.current) return;
    setInvalidEmail(null);
    setErrorPassword(null);

    if (!username || !password) {
      if (!username) {
        setInvalidEmail(t("loginPage.validation.emailRequired"));
      }
      if (!password) {
        setErrorPassword(t("loginPage.validation.passwordRequired"));
      }
      return;
    }

    const payload = {
      username: username ? username.trim().toLowerCase() : username,
      password: password,
    };

    if (payload.username && payload.password) {
      setErrorPassword();
      setInvalidEmail();
      setIsLoading(true);
      const controller = new AbortController();
      loginRequestRef.current = controller;
      axios
        .get(configData.BASE_URL + "api/v1/users/login", {
          auth: payload,
          withCredentials: true,
          signal: controller.signal,
        })
        .then((response) => {
          if (loginRequestRef.current !== controller) return;
          if (response.status === 200) {
            response.data.isAuthenticated = true;
            auth.login(response.data.accessToken);
            response.data.userType === "expeditor"
                ? navigate("/orders/available")
                : navigate("/main");
          }
        })
        .catch((error) => {
          if (loginRequestRef.current !== controller) return;
          if (error?.response?.data?.errorCode) {
            setInvalidEmail(error.response.data.errorText);
            setErrorPassword(error.response.data.errorText);
          } else {
            setErrorPassword(t("loginPage.validation.invalidCredentials"));
            setInvalidEmail(t("loginPage.validation.invalidCredentials"));
          }
        })
        .finally(() => {
          if (loginRequestRef.current === controller) {
            loginRequestRef.current = null;
            setIsLoading(false);
          }
        });
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      if (type === "username") {
        setCopiedUsername(true);
        setTimeout(() => setCopiedUsername(false), 2000);
      }
      if (type === "password") {
        setCopiedPassword(true);
        setTimeout(() => setCopiedPassword(false), 2000);
      }
    });
  };

  return (
    <>
      <div className={styles.containerReg}>
        <div className={styles.containerReg_wallpaper}>
          <div className={styles.card}>
            <div className={styles.card_topbar}>
              <div className={styles.card_logo}>
                <img src="/icons/ic_biny_dark.svg" alt="logo" />
              </div>
              <LanguageDropdown />
            </div>
            <div className={styles.card_content}>
              <div className={styles.card_title}>{t("loginPage.title")}</div>
              <div className={styles.card_form}>
                <div ref={usernameInputRef} style={{ position: "relative", width: "100%" }}>
                  <CustomInput
                    placeholder={t("loginPage.email.placeholder")}
                    labelText={t("loginPage.email.label")}
                    inputType="text"
                    isDeleteText={true}
                    onChangeValue={setUsername}
                    value={username}
                    errorText={invalidEmail}
                  />

                  {location.state?.username && (
                    <div className={styles.popper}>
                      <span>
                        {t("loginPage.email.label")}: {location.state.username}
                      </span>
                      <button
                        onClick={() => copyToClipboard(location.state.username, "username")}
                        className={styles.copyButton}
                      >
                        {copiedUsername ? <Check color="#6D7885" /> : <CopyIconNew />}
                      </button>
                    </div>
                  )}
                </div>

                <div ref={passwordInputRef} style={{ position: "relative", width: "100%" }}>
                  <CustomInput
                    placeholder={t("loginPage.password.placeholder")}
                    labelText={t("loginPage.password.label")}
                    inputType="text"
                    isPasswordEye={true}
                    onKeyPress={handleKeyPress}
                    value={password}
                    onChangeValue={setPassword}
                    errorText={errorPassword}
                  />

                  {location.state?.password && (
                    <div className={styles.popper}>
                      <span>
                        {t("loginPage.password.label")}: {location.state.password}
                      </span>
                      <button
                        onClick={() => copyToClipboard(location.state.password, "password")}
                        className={styles.copyButton}
                      >
                        {copiedPassword ? <Check color="#6D7885" /> : <CopyIconNew />}
                      </button>
                    </div>
                  )}
                </div>

                <div className={styles.card_text}>
                  <span>{t("loginPage.forgotPassword.prompt")}</span>
                  <Link to="/forgot-password">{t("loginPage.forgotPassword.link")}</Link>
                </div>
              </div>
              <div className={styles.btns}>
                <CustomButton
                  isFall
                  text={t("loginPage.buttons.login")}
                  isLoading={isLoading}
                  onClick={handleLogin}
                />
                <div className={styles.btns_text}>
                  <div className={styles.btns_before}> </div>
                  <div className={styles.btns_textDashes}>{t("loginPage.register.prompt")}</div>
                  <div className={styles.btns_before}></div>
                </div>
                <CustomButton
                  isFall
                  text={t("loginPage.buttons.register")}
                  isWhite={true}
                  onClick={() => navigate("/registration")}
                />
              </div>
            </div>
            <div className={styles.card_footer}>
              <span>
                {t("loginPage.footer.agreementPrefix")}
                <a href="https://saas.biny.co//offer-agreement.pdf" target="_blank" rel="noopener noreferrer">
                  {t("loginPage.footer.agreementLink")}
                </a>
                {t("loginPage.footer.agreementSuffix")}
              </span>
            </div>
          </div>
          <div className={styles.right}>
            <div className={styles.banner}>
              <div className={styles.banner_dec}>
                <div className={styles.banner_title}>{t("loginPage.banner.title")}</div>
                <div className={styles.banner_text}>{t("loginPage.banner.text")}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
